import axios from 'lib/axios'
import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { usePackageStore } from './packageStore'

export const FLAGGABLE_TYPES = {
  REQUIREMENT: 'PackageRequirement',
  DOCUMENT: 'PackageDocument',
}

const FLAG_MAP_KEYS = {
  [FLAGGABLE_TYPES.REQUIREMENT]: 'requirementFlags',
  [FLAGGABLE_TYPES.DOCUMENT]: 'documentFlags',
}

// Builds { requirementFlags, documentFlags } maps keyed by flaggable_id from a flags array.
const buildFlagMaps = (flags) => {
  const maps = { requirementFlags: {}, documentFlags: {} }
  for (const f of flags) {
    const key = FLAG_MAP_KEYS[f.flaggable_type]
    if (!key) continue
    const map = maps[key]
    const arr = (map[f.flaggable_id] ??= [])
    arr.push(f)
  }
  return maps
}

// Intercepts set calls — if the update contains `flags`, rebuilds the requirementFlags, documentFlags maps automatically.
const withFlagMaps = (config) => (set, get, api) =>
  config(
    (partial, replace, name) => {
      const next = typeof partial === 'function' ? partial(get()) : partial
      set(Object.hasOwn(next, 'flags') ? { ...next, ...buildFlagMaps(next.flags) } : next, replace, name)
    },
    get,
    api
  )

export const useFlagStore = create(
  devtools(
    subscribeWithSelector(
      withFlagMaps((set, get) => ({
        flags: [],
        requirementFlags: {},
        documentFlags: {},
        loading: false,
        everLoaded: false,
        error: null,

        // Each set() call passes an action name as the 3rd arg — this labels the action in Redux DevTools instead of showing "anonymous".
        loadFlags: async (packageId) => {
          if (get().loading) return
          set({ loading: true, error: null }, false, 'loadFlags/pending')
          try {
            const { data } = await axios.get(`/api/packages/${packageId}/flags`)
            set({ flags: data.flags }, false, 'loadFlags/fulfilled')
          } catch (err) {
            set({ error: err }, false, 'loadFlags/rejected')
          } finally {
            set({ loading: false, everLoaded: true }, false, 'loadFlags/done')
          }
        },

        setFlags: (flags) => set({ flags }, false, 'setFlags'),
        addFlag: (flag) => set((s) => ({ flags: [...s.flags, flag] }), false, 'addFlag'),
        updateFlag: (id, changes) =>
          set((s) => ({ flags: s.flags.map((f) => (f.id === id ? { ...f, ...changes } : f)) }), false, 'updateFlag'),
        removeFlag: (id) => set((s) => ({ flags: s.flags.filter((f) => f.id !== id) }), false, 'removeFlag'),
      }))
    ),
    { name: 'flagStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

export const selectFlagsForTarget = (type, id) => (s) =>
  s.flags.filter((f) => f.flaggable_type === type && f.flaggable_id === id)

export const selectAllFlags = (s) => s.flags
export const selectLoading = (s) => s.loading
export const selectFlagsEverLoaded = (s) => s.everLoaded
export const selectError = (s) => s.error

// Returns all flags for a single requirement id (array, stable ref via useShallow)
export const selectRequirementFlags = (id) => (s) => s.requirementFlags[id] ?? []

// Returns all flags for a single document id (array, stable ref via useShallow)
export const selectDocumentFlags = (id) => (s) => s.documentFlags[id] ?? []

// Returns all flags scoped to a requirement: requirement-level flags + all document flags for the given document IDs
export const selectFlagsForRequirementScope = (requirementId, documentIds = []) => (s) => {
  const reqFlags = s.requirementFlags[requirementId] ?? []
  const docFlags = documentIds.flatMap((docId) => s.documentFlags[docId] ?? [])
  return [...reqFlags, ...docFlags]
}

// React-to-jQuery bridge — runs once at module load, not inside a component

function buildDocumentFlagsByRequirementId(documentFlags) {
  const result = {}
  const requirements = usePackageStore.getState().package?.visible_package_requirements ?? []
  for (const req of requirements) {
    for (const doc of req.package_documents ?? []) {
      const flags = documentFlags[doc.id]
      if (flags?.length) {
        ;(result[req.id] ??= []).push(...flags)
      }
    }
  }
  return result
}

function dispatchFlagsUpdated() {
  const { flags, requirementFlags, documentFlags } = useFlagStore.getState()
  document.dispatchEvent(
    new CustomEvent('flags:updated', {
      detail: {
        flags,
        flagsByRequirementId: requirementFlags,
        flagsByDocumentId: documentFlags,
        documentFlagsByRequirementId: buildDocumentFlagsByRequirementId(documentFlags),
      },
    })
  )
}

// Re-dispatch when flags change
useFlagStore.subscribe((state) => state.flags, dispatchFlagsUpdated)

// Re-dispatch when package data loads — covers the race where package loads after flags
usePackageStore.subscribe((state) => state.package, dispatchFlagsUpdated)
